import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Button } from '../../components/ui/button';
import { Minus, Plus, Users } from 'lucide-react';

const fmtINR = (n) => !isFinite(n) ? '₹0.00' : '₹' + (Math.round(n * 100) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TipCalculator = () => {
  const [bill, setBill] = useState(1250);
  const [tipPct, setTipPct] = useState(10);
  const [people, setPeople] = useState(4);
  const [round, setRound] = useState(false);

  const { tip, total, perPerson, tipPerPerson } = useMemo(() => {
    let t = (bill * tipPct) / 100;
    let tot = bill + t;
    if (round) tot = Math.ceil(tot / 10) * 10;
    const adjustedTip = tot - bill;
    return {
      tip: adjustedTip,
      total: tot,
      perPerson: people > 0 ? tot / people : 0,
      tipPerPerson: people > 0 ? adjustedTip / people : 0,
    };
  }, [bill, tipPct, people, round]);

  return (
    <CalculatorLayout
      title="Tip Calculator"
      subtitle="Split restaurant bills and calculate tips easily. Optimized for Indian dining."
      category="Other"
      description={<p>Calculate how much to tip and split the bill with friends. In India, tipping is typically 5–10% of the bill amount, often already included as service charge in upscale restaurants. Always check your bill before tipping again.</p>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-5">
          <div>
            <Label className="text-sm font-medium mb-2 block">Bill Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">₹</span>
              <Input type="number" value={bill} onChange={(e) => setBill(Math.max(0, +e.target.value))} className="pl-7 text-lg font-semibold h-11" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Tip Percentage</Label>
              <span className="text-saffron-700 font-bold display-font">{tipPct}%</span>
            </div>
            <Slider min={0} max={30} step={1} value={[tipPct]} onValueChange={(v) => setTipPct(v[0])} />
            <div className="flex gap-2 mt-3 flex-wrap">
              {[0, 5, 10, 15, 20].map(p => (
                <Button key={p} size="sm" variant={tipPct === p ? 'default' : 'outline'} onClick={() => setTipPct(p)} className={tipPct === p ? 'bg-saffron-500 hover:bg-saffron-600 text-white' : 'border-slate-300'}>
                  {p}%
                </Button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium flex items-center gap-1.5"><Users className="w-4 h-4" /> People</Label>
              <span className="text-indgreen-700 font-bold display-font">{people}</span>
            </div>
            <div className="flex items-center gap-3">
              <Button size="icon" variant="outline" onClick={() => setPeople(Math.max(1, people - 1))} className="h-9 w-9"><Minus className="w-4 h-4" /></Button>
              <div className="flex-1 text-center font-display text-2xl font-bold text-bharat-navy display-font">{people}</div>
              <Button size="icon" variant="outline" onClick={() => setPeople(people + 1)} className="h-9 w-9"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
            <input type="checkbox" checked={round} onChange={(e) => setRound(e.target.checked)} className="accent-saffron-500 w-4 h-4" />
            Round up total to nearest ₹10
          </label>
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/30 rounded-xl border border-slate-200 p-5">
          <div className="space-y-4">
            <Row label="Tip Amount" value={fmtINR(tip)} tone="saffron" />
            <Row label="Total Bill" value={fmtINR(total)} tone="navy" big />
            <div className="pt-3 border-t border-slate-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Per Person (× {people})</div>
              <Row label="Tip / person" value={fmtINR(tipPerPerson)} tone="saffron" />
              <Row label="Total / person" value={fmtINR(perPerson)} tone="green" big />
            </div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Row = ({ label, value, tone, big }) => {
  const color = tone === 'saffron' ? 'text-saffron-700' : tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`display-font font-bold ${big ? 'text-2xl md:text-3xl' : 'text-lg'} ${color}`}>{value}</span>
    </div>
  );
};

export default TipCalculator;
